import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar } from 'lucide-react';
import { PostCard } from '@/components/PostCard';
import { BottomNav } from '@/components/BottomNav';
import { CreatePostModal } from '@/components/CreatePostModal';
import { Avatar } from '@/components/Avatar';
import { FollowButton } from '@/components/FollowButton';

const UserProfilePage = () => {
  const { username } = useParams();
  const { user } = useContext(AppContext);
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUserProfile();
    fetchUserIdeas();
  }, [username]);

  const fetchUserProfile = async () => {
    try {
      // Search for user by username
      const response = await axios.get('/ideas');
      const allIdeas = response.data;
      const userIdea = allIdeas.find(idea => idea.username === username);
      
      if (userIdea) {
        setProfileUser({
          id: userIdea.user_id,
          username: userIdea.username,
        });
      }
    } catch (error) {
      toast.error('Failed to fetch user profile');
    }
  };

  const fetchUserIdeas = async () => {
    try {
      const response = await axios.get('/ideas');
      const userIdeas = response.data.filter(idea => idea.username === username);
      setIdeas(userIdeas);
    } catch (error) {
      toast.error('Failed to fetch user ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (ideaId, voteType) => {
    try {
      await axios.post(`/ideas/${ideaId}/vote`, { vote_type: voteType });
      fetchUserIdeas();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleCreatePost = async (formData) => {
    setIsSubmitting(true);
    try {
      await axios.post('/ideas', formData);
      toast.success('Idea posted successfully!');
      setIsModalOpen(false);
      fetchUserIdeas();
    } catch (error) {
      toast.error('Failed to post idea');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-lg animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-8">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="bg-white border-b border-cardBorder sticky top-0 z-40"
      >
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-surface rounded-lg transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 text-grayDark" />
          </button>
          
          {/* Profile Header */}
          {profileUser && (
            <div className="flex items-start gap-4">
              <Avatar username={profileUser.username} size="xl" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-baloo font-bold text-primaryDark">
                    @{profileUser.username}
                  </h1>
                  {user && (
                    <FollowButton 
                      userId={profileUser.id}
                      username={profileUser.username}
                      size="md"
                      currentUser={user}
                    />
                  )}
                </div>
                <div className="text-sm text-grayMeta">
                  {ideas.length} {ideas.length === 1 ? 'idea' : 'ideas'} posted
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.header>

      {/* Ideas */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {ideas.length === 0 ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-sm p-12 text-center border border-cardBorder"
          >
            <h3 className="font-baloo font-bold text-xl text-primaryDark mb-2">No ideas yet</h3>
            <p className="text-gray">@{username} hasn't posted any ideas yet.</p>
          </motion.div>
        ) : (
          ideas.map((idea, index) => (
            <PostCard 
              key={idea.id} 
              idea={idea} 
              index={index} 
              onVote={handleVote}
              currentUser={user}
            />
          ))
        )}
      </main>

      {/* Bottom Nav */}
      <BottomNav onCreateClick={() => setIsModalOpen(true)} />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreatePost}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default UserProfilePage;
